<template>
  <div v-if="!isLoading && booking" class="space-y-4">
    <h2>
      {{ insuranceCase.status === InsuranceCaseStatus.RETURNED ? t('close') : t('invoices') }}
    </h2>
    <Invoices
      :booking="booking"
      :filter-invoice-types="[InvoiceType.OTHER]"
      hide-add-invoice-button
      hide-cancel-edit-button
      default-expanded
      allow-customer-selection
    />
    <div class="flex justify-end gap-4">
      <CVButton @click="handleGenerateInvoices">{{ t('regenerateInvoices') }}</CVButton>
      <CVButton
        v-if="booking.insuranceCase?.status === InsuranceCaseStatus.RETURNED"
        variant="default"
        :disabled="!canBeClosed"
        :loading="isUpdating"
        @click="handleClose"
      >
        {{ t('closeNow') }}
      </CVButton>
    </div>
  </div>
  <div v-else>
    <Spinner class="h-8 w-8" />
  </div>
</template>
<script setup lang="ts">
import { useBooking } from '@/queries/use-bookings';
import { useI18n } from 'vue-i18n';
import Invoices from '@/components/booking-forms/Invoices.vue';
import { computed } from 'vue';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import { useInvoicesOfBooking } from '@/hooks/use-invoices-of-booking';
import {
  useGenerateInsuranceCaseInvoices,
  useUpdateInsuranceCase,
} from '@/queries/use-insurance-cases';
import { type InsuranceCase, InsuranceCaseStatus, InvoiceType } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';

const props = defineProps<{
  insuranceCase: InsuranceCase;
}>();

const { t } = useI18n();

const { data: booking, isLoading } = useBooking(props.insuranceCase.booking!.id);
const { invoices, isLoading: isLoadingInvoices } = useInvoicesOfBooking(
  props.insuranceCase.booking!.id,
);

const canBeClosed = computed(
  () =>
    !isLoadingInvoices.value &&
    !invoices.value.some((invoice) => invoice.status === InvoiceStatus.DRAFT),
);

const { mutateAsync: update, isPending: isUpdating } = useUpdateInsuranceCase();
const { mutateAsync: generateInvoices } = useGenerateInsuranceCaseInvoices();

const handleClose = async () => {
  await update({
    id: props.insuranceCase.id,
    insuranceCase: { status: InsuranceCaseStatus.CLOSED },
  });
};

const handleGenerateInvoices = async () => {
  const insuranceProvider = props.insuranceCase.insuranceProvider;

  let inputOptions: Record<string, string> | null = null;
  if (insuranceProvider === 'axa') {
    inputOptions = {
      all: t('all'),
      axa: t('axa'),
      porsche: t('porsche'),
    };
  } else if (insuranceProvider === 'europ') {
    inputOptions = {
      all: t('all'),
      europ: t('europ'),
      porsche: t('porsche'),
    };
  }

  const result = await Alert.fire<string>({
    titleText: t('reallyGenerateInvoicesTitle'),
    html: `${t('reallyGenerateInvoicesText')}${inputOptions ? '<br><br>' + t('selectInvoiceOptionsText') : ''}`,
    icon: 'question',
    ...(inputOptions
      ? {
          input: 'select',
          inputOptions,
          inputValue: 'all',
          inputValidator: (value) => (!value ? t('pleaseSelectOption') : null),
        }
      : {}),
    buttons: [
      {
        propertyName: 'isConfirmed',
        text: t('generate'),
        style: 'default',
        needsValidation: true,
      },
      {
        propertyName: 'isDismissed',
        text: t('cancel'),
        style: 'ghost',
      },
    ],
  });

  if (result.isConfirmed) {
    await generateInvoices({
      id: props.insuranceCase.id,
      data: {
        data: {
          insuranceProvider: insuranceProvider as any,
          invoicesToGenerate: result.value as any,
        },
      },
    });
  }
};
</script>
<i18n lang="json">
{
  "en": {
    "close": "Close Insurance Case",
    "invoices": "Invoices",
    "closeNow": "Close Insurance Case Now",
    "regenerateInvoices": "Regenerate Invoices",
    "reallyGenerateInvoicesTitle": "Really generate Invoices?",
    "reallyGenerateInvoicesText": "Already existing invoices will be regenerated. Please make sure to delete or cancel duplicate, unused invoices.",
    "selectInvoiceOptionsText": "Which invoices should be regenerated?",
    "generate": "Generate",
    "cancel": "Cancel",
    "pleaseSelectOption": "Please select an option",
    "axa": "AXA",
    "porsche": "Porsche",
    "all": "All",
    "europ": "Europ"
  },
  "de": {
    "close": "Versicherungsfall schließen",
    "invoices": "Rechnungen",
    "closeNow": "Versicherungsfall jetzt schließen",
    "regenerateInvoices": "Rechnungen erneut generieren",
    "reallyGenerateInvoicesTitle": "Rechnungen wirklich neu generieren?",
    "reallyGenerateInvoicesText": "Bereits vorhandene Rechnungen werden nochmal generiert. Bitte sorge dafür, dass doppelte, nicht gebrauchte Rechnungen ggf. gelöscht oder storniert werden.",
    "selectInvoiceOptionsText": "Welche Rechnungen sollen neu generiert werden?",
    "generate": "Generieren",
    "cancel": "Abbrechen",
    "pleaseSelectOption": "Bitte wählen Sie eine Option aus",
    "axa": "AXA",
    "porsche": "Porsche",
    "all": "Alle",
    "europ": "Europ"
  },
  "es": {
    "close": "Cerrar siniestro",
    "invoices": "Facturas",
    "closeNow": "Cerrar siniestro ahora",
    "regenerateInvoices": "Volver a generar facturas",
    "reallyGenerateInvoicesTitle": "¿Seguro que desea generar las facturas?",
    "reallyGenerateInvoicesText": "Las facturas existentes se volverán a generar. Asegúrese de eliminar o anular las facturas duplicadas que no se usen.",
    "selectInvoiceOptionsText": "¿Qué facturas deben volver a generarse?",
    "generate": "Generar",
    "cancel": "Cancelar",
    "pleaseSelectOption": "Seleccione una opción",
    "axa": "AXA",
    "porsche": "Porsche",
    "all": "Todas",
    "europ": "Europ"
  }
}
</i18n>
